import { Navbar, Footer } from './LandingPage';
import { WanderfulHero } from './WanderfulHero';
import { Lock, ShieldCheck, CheckCircle2, Server, EyeOff, BookOpen } from 'lucide-react';

const TrustPage = () => {
  return (
    <div className="bg-white min-h-screen font-sans">
      <Navbar />
      <WanderfulHero 
        title="Trust and Transparency at our Core."
        subtitle="We build tools that respect privacy, ensure security, and align with the future of Indian education standards."
        cta1Text="View Certifications"
        cta1Link="#certifications"
      />

      {/* 1. Security Architecture Section */}
      <section id="security" className="py-8 sm:py-12 md:py-24 bg-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:40px_40px]" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
             <div>
                <span className="text-[10px] font-bold text-black uppercase tracking-[0.2em] bg-black/5 px-4 py-1.5 rounded-full border border-black/10">Security</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-black mt-6 mb-6 tracking-tight leading-tight">Bank-Grade Security Architecture</h2>
                <p className="text-gray-500 text-base leading-relaxed mb-8 font-medium">
                  Your data is protected with end-to-end encryption and industry-leading security protocols. We ensure that student answer sheets and institutional data are never compromised.
                </p>
                <div className="space-y-4 sm:space-y-6">
                   {[
                     { title: "AES-256 Encryption", desc: "All data at rest and in transit is encrypted using the highest industry standards.", icon: <Lock className="w-5 h-5" /> },
                     { title: "Distributed Cloud Infrastructure", desc: "Hosted on secure, compliant data centers with 99.9% uptime and redundancy.", icon: <Server className="w-5 h-5" /> },
                     { title: "Strict Access Controls", desc: "Role-based access ensure only authorized personnel can view specific evaluation data.", icon: <EyeOff className="w-5 h-5" /> }
                   ].map((item, i) => (
                     <div key={i} className="flex gap-4 items-start">
                        <div className="w-10 h-10 rounded-xl bg-black text-white flex items-center justify-center flex-shrink-0 shadow-md">
                           {item.icon}
                        </div>
                        <div>
                           <h4 className="font-bold text-black text-sm">{item.title}</h4>
                           <p className="text-xs text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                        </div>
                     </div>
                   ))}
                </div>
             </div>
             <div className="bg-[#fafafa] rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-4 sm:p-6 md:p-8 lg:p-12 border border-gray-200 relative overflow-hidden shadow-sm flex items-center justify-center min-h-[300px]">
                {/* Decorative Grid */}
                <div className="absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,#000_1px,transparent_1px),linear-gradient(to_bottom,#000_1px,transparent_1px)] bg-[size:20px_20px]" />
                <div className="relative z-10">
                   <div className="aspect-video bg-white rounded-2xl shadow-xl border border-gray-200 p-6 flex flex-col justify-center items-center text-center">
                      <ShieldCheck className="w-14 h-14 text-black mb-4" />
                      <h3 className="text-xl font-bold text-black mb-2">Secure Evaluation Pipeline</h3>
                      <p className="text-gray-400 text-xs font-semibold">Automated audits on every single paper processed.</p>
                   </div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 2. Data Privacy (DPDP Act) Section */}
      <section id="privacy" className="py-8 sm:py-12 md:py-24 bg-black text-white rounded-[60px] mx-2 sm:mx-4 md:mx-8 relative z-10 shadow-[0_20px_50px_rgba(0,0,0,0.15)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
           <div className="p-4 sm:p-6 md:p-8">
              <div className="max-w-3xl mx-auto text-center mb-16">
                 <span className="text-[10px] font-bold text-white/60 uppercase tracking-[0.2em] bg-white/10 px-4 py-1.5 rounded-full border border-white/20">Privacy</span>
                 <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-white mt-6 mb-6 tracking-tight">Fully Compliant with India's DPDP Act</h2>
                 <p className="text-gray-400 text-base leading-relaxed font-medium">
                   We prioritize the Digital Personal Data Protection Act (DPDP) to ensure student data sovereignty and institutional peace of mind.
                 </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                 <div className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors">
                    <h4 className="font-bold text-white mb-3 text-lg">Data Minimization</h4>
                    <p className="text-xs text-gray-400 leading-relaxed font-medium">We only collect the data necessary for evaluation. Anonymized scanning ensures names and IDs are treated with utmost care.</p>
                 </div>
                 <div className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors">
                    <h4 className="font-bold text-white mb-3 text-lg">Right to Erasure</h4>
                    <p className="text-xs text-gray-400 leading-relaxed font-medium">Institutions have full control over data retention policies, with the ability to delete student records at any time.</p>
                 </div>
                 <div className="bg-white/5 border border-white/10 rounded-3xl p-8 hover:bg-white/10 transition-colors">
                    <h4 className="font-bold text-white mb-3 text-lg">Consent Management</h4>
                    <p className="text-xs text-gray-400 leading-relaxed font-medium">Built-in workflows to manage student and parent consent for evaluation processing, as per DPDP guidelines.</p>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* 3. NEP 2020 Alignment Section */}
      <section id="nep" className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
              <div className="order-2 lg:order-1">
                 <div className="relative aspect-square max-w-md mx-auto">
                    <div className="absolute inset-0 bg-gray-100 rounded-full blur-[80px] opacity-40" />
                    <div className="relative z-10 bg-[#fafafa] border border-gray-200 rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-6 sm:p-8 lg:p-10 h-full flex flex-col justify-center shadow-sm">
                       <BookOpen className="w-12 h-12 text-black mb-6" />
                       <h3 className="text-2xl font-bold text-black mb-4 tracking-tight">Focus on Competency-Based Assessment</h3>
                       <p className="text-gray-500 text-sm leading-relaxed font-medium">
                          Our AI models are designed to evaluate higher-order thinking skills, application of knowledge, and critical analysis, moving away from rote learning—exactly what NEP 2020 advocates.
                       </p>
                    </div>
                 </div>
              </div>
              <div className="order-1 lg:order-2">
                 <span className="text-[10px] font-bold text-black uppercase tracking-[0.2em] bg-black/5 px-4 py-1.5 rounded-full border border-black/10">Alignment</span>
                 <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-black mt-6 mb-6 tracking-tight">Designed for NEP 2020</h2>
                 <p className="text-gray-500 text-base leading-relaxed mb-8 font-medium">
                    Parakh AI supports the transition to holistic progress cards and competency-based evaluation frameworks mandated by the National Education Policy.
                 </p>
                 <ul className="space-y-4">
                    <li className="flex items-center gap-3 text-black font-semibold">
                       <CheckCircle2 className="w-5 h-5 text-black" /> Qualitative Feedback Loops
                    </li>
                    <li className="flex items-center gap-3 text-black font-semibold">
                       <CheckCircle2 className="w-5 h-5 text-black" /> Topic-wise Skill Mapping
                    </li>
                    <li className="flex items-center gap-3 text-black font-semibold">
                       <CheckCircle2 className="w-5 h-5 text-black" /> Support for Regional Languages
                    </li>
                 </ul>
              </div>
           </div>
        </div>
      </section>

      {/* 4. Certifications Section */}
      <section id="certifications" className="py-8 sm:py-12 md:py-24 bg-black text-white rounded-t-[80px] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
           <div className="text-center mb-16">
              <span className="text-[10px] font-bold text-white/50 uppercase tracking-[0.2em] bg-white/10 px-4 py-1.5 rounded-full border border-white/20">Compliance</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold mt-6 mb-6 tracking-tight">Globally Recognized Certifications</h2>
              <p className="text-gray-400 max-w-2xl mx-auto text-base font-medium">
                 We hold ourselves to the highest global standards for data security and privacy management.
              </p>
           </div>

           <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-6 sm:p-8 lg:p-10 flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-300">
                 <img src="/ISO_27001_Final-Logo-removebg-preview.png" alt="ISO 27001" className="h-28 object-contain mb-8 group-hover:scale-105 transition-transform" />
                 <h4 className="text-lg font-bold mb-2">ISO 27001</h4>
                 <p className="text-white/40 text-xs font-medium leading-relaxed">International standard for information security management systems (ISMS).</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-6 sm:p-8 lg:p-10 flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-300">
                 <img src="/soc2-t2-compliance-badge-removebg-preview.png" alt="SOC2 Type 2" className="h-28 object-contain mb-8 group-hover:scale-105 transition-transform" />
                 <h4 className="text-lg font-bold mb-2">SOC2 Type II</h4>
                 <p className="text-white/40 text-xs font-medium leading-relaxed">Independent audit validating our security, availability, and confidentiality controls.</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl sm:rounded-[24px] md:rounded-[40px] p-6 sm:p-8 lg:p-10 flex flex-col items-center text-center group hover:bg-white/10 transition-all duration-300">
                 <img src="/gdpr-compliant-removebg-preview.png" alt="GDPR Compliant" className="h-28 object-contain mb-8 group-hover:scale-105 transition-transform" />
                 <h4 className="text-lg font-bold mb-2">GDPR Compliant</h4>
                 <p className="text-white/40 text-xs font-medium leading-relaxed">Adherence to strict data protection and privacy regulations for EU and global users.</p>
              </div>
           </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TrustPage;
